import { createFrame } from "@/api/spaces";
import { getFrameDefaultSize } from "@/lib/frame-defaults";
import { ingestFrame } from "@/lib/frame-ingest";
import { newTextFrameFields } from "@/components/shapes/doc-frame/new-text-frame";
import { useCanvasStore } from "@/stores/canvas-store";
import { useNotificationStore } from "@/stores/notification-store";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { translate } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";

type CreatedFrame = Awaited<ReturnType<typeof createFrame>>;
const DOC_SIZE = getFrameDefaultSize("doc");

interface CreateDocFrameOptions {
  projectId: string;
  spaceId: string;
  position?: { x: number; y: number };
  text?: string;
  selectFrame: (frameId: string | null) => void;
}

export async function createDocFrame({
  projectId,
  spaceId,
  position,
  text = "",
  selectFrame,
}: CreateDocFrameOptions): Promise<CreatedFrame | null> {
  try {
    const frame = await createFrame(projectId, spaceId, {
      ...newTextFrameFields(text),
      title: "",
      ...(position ? { position_x: position.x, position_y: position.y } : {}),
      width: DOC_SIZE.w,
      height: DOC_SIZE.h,
    });
    ingestFrame(frame);
    selectFrame(frame.id);

    return frame;
  } catch (frameErr) {
    const msg =
      frameErr instanceof Error
        ? frameErr.message
        : translate(canvasMessages, "unknownError");
    useNotificationStore.getState().addSystemToast({
      id: `note-create-fail-${Date.now()}`,
      level: "error",
      title: translate(canvasMessages, "createNoteFailed"),
      description: msg,
    });
    return null;
  }
}

export async function createDocFrameAtNextPosition(
  projectId: string,
  spaceId: string,
) {
  const { selectFrame } = useCanvasStore.getState();

  const frame = await createDocFrame({
    projectId,
    spaceId,
    selectFrame,
  });
  if (!frame) return;

  workspaceViewAdapter().revealFrames([frame.id]);
}
